"use client";

import { c as _c } from "react/compiler-runtime";
import { Breadcrumbs } from "@/components/common/breadcrumbs";
import { CallToAction } from "@/components/common/call-to-action";
import { Icon } from "@/components/common/icon";
import { RenderIf } from "@/components/common/render-if";
import { SeoHeading } from "@/components/common/seo-heading";
import { Container } from "@/components/container";
import { Rating } from "@/components/reui/rating";
import { Section } from "@/components/section";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Text } from "@/components/ui/text";
import { getSystemIcon } from "@/lib/system-lucide-icon-map";
import { cn } from "@/lib/utils";
import { usePopup } from "@/providers/popup-provider";
import { Award, Briefcase, Globe, Headset, IdCard, ShieldCheck, Star, Users } from "lucide-react";
import Image from "next/image";
type ItsmCourseBannerProps = {
  section: PageSections;
  data: {
    seoHeading: string;
    heading: string;
    description: string;
    backgroundBannerImage?: string;
    imageAltText?: string;
    imagePath?: string;
    breadcrumbs: BreadcrumbsType[];
    serviceFlags?: string[];
    serviceId: number | string;
    categoryName?: string;
    serviceAccreditations: Accreditation[];
    cards: {
      icon: string;
      label: string;
      text: string;
    }[];
    stats: {
      label: string;
      value: string;
    }[];
  };
};
const statsIcon = [IdCard, Star, Users, Globe, Award];
const bgColorMapping = {
  orange: "bg-orange-50 dark:bg-background",
  blue: "bg-blue-50 dark:bg-background",
  rose: "bg-rose-50 dark:bg-background"
};
const ItsmCourseBanner = t0 => {
  const $ = _c(73);
  const {
    section,
    data
  } = t0;
  let t1;
  if ($[0] !== data.serviceFlags) {
    t1 = data?.serviceFlags?.includes("NEW_LAUNCHED");
    $[0] = data.serviceFlags;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isNewLaunched = Boolean(t1);
  const {
    openPopup
  } = usePopup();
  let t2;
  if ($[2] !== data.serviceId || $[3] !== openPopup) {
    t2 = () => {
      openPopup({
        type: "corporate-enquiry",
        title: "Corporate Enquiry",
        description: "Submit this form to get in touch with our corporate team.",
        data: {
          serviceId: data.serviceId
        }
      });
    };
    $[2] = data.serviceId;
    $[3] = openPopup;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleCorporateEnquiry = t2;
  const t3 = section.sectionId;
  let t4;
  if ($[5] !== section.sectionClass) {
    t4 = cn(section.sectionClass, bgColorMapping.rose, "py-6 sm:py-10 md:py-15");
    $[5] = section.sectionClass;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== data.categoryName) {
    t5 = <Badge className="bg-primary/10 text-primary dark:bg-primary/30 dark:text-primary mb-4">{data.categoryName}</Badge>;
    $[7] = data.categoryName;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== data.heading || $[10] !== data.seoHeading) {
    t6 = <SeoHeading heading={data.heading} seoHeading={data.seoHeading} as="h1" />;
    $[9] = data.heading;
    $[10] = data.seoHeading;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("typeset", "mb-4", "[&_ul]:ps-0", "[&_ul]:list-none [&_ul]:space-y-2", "[&_li]:relative [&_li]:ml-7", "[&_li]:before:content-['']", "[&_li]:before:absolute", "[&_li]:before:-left-7.5", "[&_li]:before:top-1/2", "[&_li]:before:-translate-y-1/2", "[&_li]:before:size-6", "[&_li]:before:bg-primary", "[&_li]:before:[mask:url('/images/check-circle.svg')]", "[&_li]:before:mask-no-repeat", "[&_li]:before:mask-center", "[&_li]:before:mask-contain", "[&_li]:before:[-webkit-mask:url('/images/check-circle.svg')]", "[&_li]:before:[-webkit-mask-repeat:no-repeat]", "[&_li]:before:[-webkit-mask-position:center]", "[&_li]:before:[-webkit-mask-size:contain]");
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== data.description) {
    t8 = <Text className={t7}>{data.description}</Text>;
    $[13] = data.description;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== data.cards) {
    t9 = data.cards?.map(_temp);
    $[15] = data.cards;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <div className="mb-6 grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">{t9}</div>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== data.serviceId || $[20] !== openPopup) {
    t11 = <div className="w-full sm:w-auto"><CallToAction onSaveText="Enroll Now" onCancelText="Talk to an Expert" size="lg" onSaveLink="#date-and-price" onCancel={() => {
        openPopup({
          type: "talk-to-advisor",
          title: "Talk to a Learning Advisor",
          description: "Submit this form to trigger an advisor callback to your phone.",
          data: {
            serviceId: data.serviceId
          }
        });
      }} /></div>;
    $[19] = data.serviceId;
    $[20] = openPopup;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="inline-flex gap-2"><Rating rating={4.8} /><span className="font-bold">4.8</span> (1200+ reviews)</div>;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t11) {
    t13 = <div className="flex flex-wrap items-center gap-4">{t11}{t12}</div>;
    $[23] = t11;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== handleCorporateEnquiry) {
    t14 = <div className="mt-3 text-sm">Need Customized Corporate Training?{" "}<span onClick={handleCorporateEnquiry} className="font-semibold text-green-700 underline hover:cursor-pointer">Request a Quote</span></div>;
    $[25] = handleCorporateEnquiry;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t10 || $[28] !== t13 || $[29] !== t14 || $[30] !== t5 || $[31] !== t6 || $[32] !== t8) {
    t15 = <div className="col-span-12 sm:col-span-8">{t5}{t6}{t8}{t10}{t13}{t14}</div>;
    $[27] = t10;
    $[28] = t13;
    $[29] = t14;
    $[30] = t5;
    $[31] = t6;
    $[32] = t8;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = Boolean(data.imagePath);
  let t17;
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="bg-primary/15 absolute -top-12 -left-12 size-80 rounded-full dark:bg-white/10" />;
    t18 = <div className="absolute -right-8 -bottom-8 size-48 rounded-full bg-rose-300/30 dark:bg-white/15" />;
    $[34] = t17;
    $[35] = t18;
  } else {
    t17 = $[34];
    t18 = $[35];
  }
  const t19 = data.serviceAccreditations?.length > 0;
  let t20;
  if ($[36] !== data.serviceAccreditations) {
    t20 = data?.serviceAccreditations?.map(_temp2);
    $[36] = data.serviceAccreditations;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t20) {
    t21 = <div className="absolute right-2 bottom-2 z-10 flex flex-wrap gap-2">{t20}</div>;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t19 || $[41] !== t21) {
    t22 = <RenderIf condition={t19}>{t21}</RenderIf>;
    $[40] = t19;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="pointer-events-none absolute top-0 right-0 z-10 size-32 overflow-hidden"><div className="bg-primary text-primary-foreground absolute top-7 -right-10 flex w-40 rotate-45 items-center justify-center gap-1.5 py-1 text-center text-[10px] font-bold tracking-wider uppercase shadow-md sm:text-[8px]"><span className="relative flex size-2 items-center justify-center"><span className="absolute inline-flex size-full animate-ping rounded-full bg-white opacity-75" /><span className="relative inline-flex size-1.5 rounded-full bg-white" /></span>Newly Launched</div></div>;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== isNewLaunched) {
    t24 = <RenderIf condition={isNewLaunched}>{t23}</RenderIf>;
    $[44] = isNewLaunched;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  const t25 = data.imagePath || "/images/dummy/dummy1.avif";
  const t26 = data.imageAltText || "Course Image";
  let t27;
  if ($[46] !== t25 || $[47] !== t26) {
    t27 = <Image src={t25} alt={t26} fill={true} className="object-cover" sizes="500px" />;
    $[46] = t25;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== t22 || $[50] !== t24 || $[51] !== t27) {
    t28 = <div className="relative">{t17}{t18}<AspectRatio ratio={1} className="relative h-full w-full overflow-hidden rounded-3xl shadow-2xl">{t22}{t24}{t27}</AspectRatio></div>;
    $[49] = t22;
    $[50] = t24;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t16 || $[54] !== t28) {
    t29 = <div className="col-span-12 hidden sm:col-span-4 sm:block"><RenderIf condition={t16}>{t28}</RenderIf></div>;
    $[53] = t16;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t15 || $[57] !== t29) {
    t30 = <div className="grid grid-cols-12 items-center gap-10">{t15}{t29}</div>;
    $[56] = t15;
    $[57] = t29;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== data.breadcrumbs) {
    t31 = <Breadcrumbs breadcrumbs={data.breadcrumbs} className="-bottom-7 z-1 sm:absolute" />;
    $[59] = data.breadcrumbs;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== section.sectionId || $[62] !== t30 || $[63] !== t31 || $[64] !== t4) {
    t32 = <Section id={t3} className={t4}>{t30}{t31}</Section>;
    $[61] = section.sectionId;
    $[62] = t30;
    $[63] = t31;
    $[64] = t4;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== data.stats) {
    t33 = data.stats?.map(_temp3);
    $[66] = data.stats;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== t33) {
    t34 = <div className="dark:bg-background border-y border-blue-100 bg-white py-4 dark:border-white/10 dark:border-t-transparent"><Container><div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 sm:flex-nowrap">{t33}</div></Container></div>;
    $[68] = t33;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] !== t32 || $[71] !== t34) {
    t35 = <>{t32}{t34}</>;
    $[70] = t32;
    $[71] = t34;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  return t35;
};
export default ItsmCourseBanner;
function _temp(item, index) {
  return <div key={index} className="flex items-center gap-2 rounded-xl border border-blue-100 bg-white p-2.5 shadow-sm transition-all dark:border-white/10 dark:bg-white/5"><div className="bg-primary/10 text-primary dark:bg-primary/20 flex size-9 shrink-0 items-center justify-center rounded-lg dark:text-blue-400"><Icon name={getSystemIcon(item.icon)} className="size-5" /></div><div className="min-w-0 leading-tight"><p className="text-foreground text-xs font-semibold sm:text-sm">{item.label}</p><p className="text-muted-foreground text-xs font-medium">{item.text}</p></div></div>;
}
function _temp2(accreditation, index_0) {
  return <div key={index_0} className="dark:bg-background/80 flex items-center gap-1 rounded-lg bg-white/80 px-2 py-1 text-xs font-medium shadow-sm backdrop-blur-sm"><Image src={accreditation.imagePath} alt={accreditation.imageAltText} width={20} height={20} className="h-4 w-4 rounded-full object-cover" /><span>{accreditation.label}</span></div>;
}
function _temp3(t0, i, arr) {
  const {
    value,
    label
  } = t0;
  return <div key={label} className={cn("flex flex-1 items-center gap-3", i < arr.length - 1 && "sm:border-r sm:border-blue-100 sm:pr-6 sm:dark:border-white/10")}><div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">{(() => {
        const IconComp = statsIcon[i] ?? Globe;
        return <IconComp className="size-5" />;
      })()}</div><div className="min-w-0"><p className="text-foreground text-sm font-bold sm:text-base">{value}</p><p className="text-muted-foreground text-xs">{label}</p></div></div>;
}